import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../api/api';
import './WellAttachments.css';

export default function WellAttachments() {
  const { id } = useParams();
  const [attachments, setAttachments] = useState([]);
  const [well, setWell] = useState(null);
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const baseUrl = process.env.REACT_APP_UPLOAD_BASE_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch well details
        const wellData = await apiFetch(`/api/wells/${id}`);
        setWell(wellData);

        // Fetch attachments
        const attachmentsData = await apiFetch('/api/attachments');
        const filtered = attachmentsData.filter(att => att.WellID === parseInt(id));
        setAttachments(filtered);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };
    fetchData();
  }, [id]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    
    if (!subject) {
      alert('Subject is required');
      return;
    }
    if (!description) {
      alert('Description is required');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('wellId', id);
    formData.append('subject', subject);
    formData.append('description', description);

    try {
      await apiFetch('/api/attachments', {
        method: 'POST',
        body: formData
      });

      // Refresh attachments list
      const newAttachments = await apiFetch('/api/attachments');
      const filtered = newAttachments.filter(att => att.WellID === parseInt(id));
      setAttachments(filtered);

      // Reset form
      setFile(null);
      setSubject('');
      setDescription('');
      document.getElementById('file-input').value = '';
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload file: ' + (err.message || 'Unknown error'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      await apiFetch(`/api/attachments/${attachmentId}`, {
        method: 'DELETE'
      });
      setAttachments(prev => prev.filter(att => att.AttachmentID !== attachmentId));
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete attachment');
    }
  };

  return (
    <div className="attachments-container">
      <h2>Attachments for Well: {well?.WellName}</h2>

      <form onSubmit={handleUpload} className="upload-form">
        <div className="form-group">
          <label>File:</label>
          <input
            type="file"
            id="file-input"
            onChange={handleFileChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Subject:</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter subject"
            required
          />
        </div>
        <div className="form-group">
          <label>Description:</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description"
            required
          />
        </div>
        <button type="submit" disabled={isUploading || !file}>
          {isUploading ? 'Uploading...' : 'Upload'}
        </button>
      </form>

      <div className="attachments-list">
        <h3>Current Attachments</h3>
        {attachments.length === 0 ? (
          <p>No attachments found</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>File Name</th>
                <th>Subject</th>
                <th>Description</th>
                <th>Upload Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {attachments.map(att => (
                <tr key={att.AttachmentID}>
                  <td>
                    <a
                      href={`${baseUrl}/uploads/${att.FileName}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {att.FileName}
                    </a>
                  </td>
                  <td>{att.Subject || '-'}</td>
                  <td>{att.Description || '-'}</td>
                  <td>{new Date(att.UploadedAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      onClick={() => handleDelete(att.AttachmentID)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
} 